import {
  CLASS_DESCRIPTION_FIELD,
  COMPANY_ANNUAL_REV_FIELD,
  INDUSTRY_FIELD,
  INTERNATIONAL_VALUE,
  DATE_RANGE_FIELD
} from "../constants";
import { CYBER_CONFIG } from "../constants";
import { setBackgroundImageUrl } from "../store/slice/uiPropsSlice";
import { store } from "../store/store";
import { setQlikAppSelections, setSelectionCompAnnualRev, setSelectionIndustry, setSelectionIndustryClassName } from "../store/slice/qlikSelectionSlice";
import { getQlikApp } from "./qlikService";
import { showSnackbar } from "../store/slice/snackbarSlice";

const dispatch = store.dispatch;

// Define types for selections
interface Selection {
  name: string;
  selectionString: string;
  selections: string[];
  selectionCount: number;
}

function setQlikSelections (currentSelectionsLayout:any, appName:string) {
  let selections: Selection[] | null = null;

  if (currentSelectionsLayout.qSelectionObject.qSelections.length) {
    
    selections = currentSelectionsLayout.qSelectionObject.qSelections.map((sel: any) => ({
        qField: sel.qField,
        qSelected: sel.qSelected,
        qSelectedList: sel.qSelected.split(", "),
        qSelectedCount: sel.qSelectedCount,
    }));
  }

  dispatch(setQlikAppSelections({ appRef: appName, selections }));
  return selections;
}

export async function getQlikAppSelections(appName: string = "small", altState?: string): Promise<any | null> {
  return new Promise(async (resolve) => {
    const qlikApp = await getQlikApp(appName);

    if (qlikApp) {
      // //Get current selections
      const currentSelectionsDef = {
          "qInfo": {
              "qType": "CurrentSelection"
          },
          "qSelectionObjectDef": {},
          "qStateName": altState ? altState : "",
      }
      const currentSelections = await qlikApp.createSessionObject(currentSelectionsDef);
      const currentSelectionsLayout = await currentSelections.getLayout();
      setQlikSelections(currentSelectionsLayout, appName);
      
      //Handling new selections applied
      currentSelections.on('changed', async () => {
        const updatecurrentSelectionsLayout = await currentSelections.getLayout();
        setQlikSelections(updatecurrentSelectionsLayout, appName);
      });
      resolve(true)
    }

    resolve(false)
  })
}


export async function applySelectionByFieldName(appRef: string, fieldName: string, values: any[], altState?: string): Promise<any | null> {
  try {
    return new Promise(async (resolve) => {
      const qlikApp = await getQlikApp(appRef);
      if (qlikApp) {
        const qlikField = await qlikApp.getField(fieldName, altState);
        //Adding qText or qNum to values array based on type
        const qlikValues = values.map((val) => {
          if (typeof val === "number") {
            return { qIsNumeric: true, qNumber: val };
          }
          return { qText: val };
        })
        await qlikField.selectValues(qlikValues);
        // Special handling for qlik fields to update background image and selection store
        if(fieldName === INDUSTRY_FIELD && qlikValues[0]?.qText) {
          const industryKey = qlikValues[0].qText as keyof typeof CYBER_CONFIG.backgroundImages.industry;
          if (CYBER_CONFIG.backgroundImages.industry[industryKey]) {
            dispatch(setBackgroundImageUrl(CYBER_CONFIG.backgroundImages.industry[industryKey]));
          }
        }
        if(fieldName === COMPANY_ANNUAL_REV_FIELD) {
          dispatch(setSelectionCompAnnualRev(qlikValues[0].qText));
        }

        resolve(true);
      }
    })
  }
  catch (error) {
    console.error(`Error applying selection for field ${fieldName} in app ${appRef}:`, error);
    dispatch(showSnackbar({ reason: "error", message: CYBER_CONFIG.generalErrorMessage }));
  }
}


export async function applySelectMatch(appRef: string, fieldName: string, value: string, altState?: string, currentPath?: string | null): Promise<any | null> {
  try {
    return new Promise(async (resolve) => {
      const qlikApp = await getQlikApp(appRef);
      if (qlikApp) {
        const qlikField = await qlikApp.getField(fieldName, altState);
        //Adding qText or qNum to values array based on type
        await qlikField.toggleSelect(value, true);
        // Special handling for qlik fields to update background image and selection store
        if(fieldName === INDUSTRY_FIELD && value) {
          const industryKey = value as keyof typeof CYBER_CONFIG.backgroundImages.industry;
          if (CYBER_CONFIG.backgroundImages.industry[industryKey]) {
            dispatch(setBackgroundImageUrl(CYBER_CONFIG.backgroundImages.industry[industryKey]));
          }
          dispatch(setSelectionIndustry(value));
        }
        if(fieldName === CLASS_DESCRIPTION_FIELD) {
          dispatch(setSelectionIndustryClassName(value));
        }
        if(value === INTERNATIONAL_VALUE && currentPath === '/incident-growth') {
          let fieldtoClear = await qlikApp.getField(COMPANY_ANNUAL_REV_FIELD, altState);
          await fieldtoClear.clear();
          fieldtoClear = await qlikApp.getField(DATE_RANGE_FIELD, altState);
          await fieldtoClear.clear();
        }
        if(fieldName === COMPANY_ANNUAL_REV_FIELD) {
          dispatch(setSelectionCompAnnualRev(value));
        }

        resolve(true);
      }
    })
  }
  catch (error) {
    console.error(`Error applying selection match for field ${fieldName} in app ${appRef}:`, error);
    dispatch(showSnackbar({ reason: "error", message: CYBER_CONFIG.generalErrorMessage }));
  }
}

export async function applySelectPossible(appRef: string, fieldName: string, altState?: string): Promise<any | null> {
  try {
    return new Promise(async (resolve) => {
      const qlikApp = await getQlikApp(appRef);
      if (qlikApp) {
        const qlikField = await qlikApp.getField(fieldName, altState);
        await qlikField.selectPossible();
        // Special handling for qlik fields to update background image and selection store
        if(fieldName === INDUSTRY_FIELD) {
          dispatch(setSelectionIndustry('Market Place Integration'));
        }

        resolve(true);
      }
    })
  }
  catch (error) {
    console.error(`Error applying selection possible for field ${fieldName} in app ${appRef}:`, error);
    dispatch(showSnackbar({ reason: "error", message: CYBER_CONFIG.generalErrorMessage }));
  }
}

export async function clearFieldByName(appRef: string, fieldName: string, altState?:string): Promise<void> {
  const qlikApp = await getQlikApp(appRef);
  if (qlikApp) {
    const field = await qlikApp.getField(fieldName, altState);
    await field.clear();
    if(fieldName === INDUSTRY_FIELD) {
      dispatch(setBackgroundImageUrl(CYBER_CONFIG.backgroundImages.default));
      const field = await qlikApp.getField(CLASS_DESCRIPTION_FIELD, altState);
      await field.clear();
    }
  }
}


export async function clearAppSelections(appRef: string): Promise<void> {
  const qlikApp = await getQlikApp(appRef);
  if (qlikApp) {
    await qlikApp.clearAll();
    dispatch(setBackgroundImageUrl(CYBER_CONFIG.backgroundImages.default));
  }
}


export default {
  getQlikAppSelections,
  applySelectionByFieldName,
  clearFieldByName,
  clearAppSelections,
};